import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors} from '@dnd-kit/core';
import {SortableContext, arrayMove, sortableKeyboardCoordinates, rectSortingStrategy, useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';

import {changeOrder} from '../../../../modules/admin/database';
import getDBTypeIcon from '../../../../utils/getDBTypeIcon';
import getDisplayDBType from '../../../../utils/getDisplayDBType';
import styles from './styles/card-list.module.css';

import BoxStatus from '../../../design-system/box/BoxStatus';
import ButtonRadius5 from '../../../design-system/button/ButtonRadius5';
import {ReactComponent as PencilIcon} from '../../../../assets/design-system/pencil.svg';
import {ReactComponent as TrashIcon} from '../../../../assets/design-system/trash.svg';

// 드래그 가능한 카드 컴포넌트
const SortableCard = ({ item, setCurrentStepName, setConnectionInfo, toggleAddModal, openDeleteModal, getConnectionStatus, getBoxStatusInfo, getButtonInfo }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: item.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    const { id, aliasName, dbType, state, owner, editor, connectAt, createdAt, updatedAt } = item;

    const currentConnectionStatus = getConnectionStatus(id);
    
    const dbIconStyle = { width: '36px', height: '36px' };

    const openAddModal = (e, dbItem) => {
        e.stopPropagation(); // 상위 요소로 이벤트 전파 방지

        const { id, dbType, dbName, hostName, portNumber, accessId, password, dbFile, aliasName } = dbItem;

        toggleAddModal();
        
        setCurrentStepName('info');
        setConnectionInfo({
            id: id ?? null,
            dbType: dbType ?? '',
            dbName: dbName ?? '',
            hostName: hostName ?? '',
            portNumber: portNumber ?? '',
            accessId: accessId ?? '',
            dbPassword: password ?? '',
            dbFile: dbFile ?? null,
            dbFileName: dbFile 
                ? (dbFile.fileName || dbFile.name || '')
                : '',
            aliasName: aliasName ?? ''
        });
    };

    const clickTrashIcon = (e, dbItem) => {
        e.stopPropagation(); // 상위 요소로 이벤트 전파 방지
        openDeleteModal(dbItem.id);
    };

    return (
        <div 
            ref={setNodeRef} 
            style={style}
            className={`${styles.wrapperCard} ${isDragging ? styles.dragging : ''}`} 
        >
            <div className={styles.containerHeader}>
                <div className={styles.boxHeader} data-type='first' {...attributes} {...listeners}>
                    {getDBTypeIcon(dbType, dbIconStyle)}
                    <div>
                        <p className={styles.aliasName}>{aliasName || '-'}</p>
                        <p className={styles.dbType}>{getDisplayDBType(dbType)}</p>
                    </div>
                </div>
                <div className={styles.boxHeader}>
                    <PencilIcon className={styles.iconButton} onClick={(e) => openAddModal(e, item)} />
                    <TrashIcon className={styles.iconButton}  onClick={(e) => clickTrashIcon(e, item)} />
                </div>
            </div>

            <div className={styles.separator} {...attributes} {...listeners} />

            <div className={styles.containerInfo} {...attributes} {...listeners}>
                <InfoRow
                    items={[
                        { label: '최근 연결 날짜', value: connectAt || '-' },
                        { 
                            label: '연결 상태',
                            value:
                                <BoxStatus
                                    status={getBoxStatusInfo(currentConnectionStatus, state).status}
                                    icon={getBoxStatusInfo(currentConnectionStatus, state).icon}
                                    text={getBoxStatusInfo(currentConnectionStatus, state).text}
                                />
                        }
                    ]}
                />

                <InfoRow
                    items={[
                        { label: '담당자', value: owner || '-' },
                        { label: '최근 수정자', value: editor || '-' }
                    ]}
                />

                <InfoRow
                    items={[
                        { label: '생성일', value: createdAt || '-' },
                        { label: '수정일', value: updatedAt || '-' }
                    ]}
                />
            </div>

            <ButtonRadius5
                height='H40'
                status={getButtonInfo(currentConnectionStatus, id).status}
                text={getButtonInfo(currentConnectionStatus, id).text}
                handleClick={getButtonInfo(currentConnectionStatus, id).handleClick}
            />
        </div>
    );
};

export default function CardList({ response, setCurrentStepName, setConnectionInfo, toggleAddModal, openDeleteModal, getConnectionStatus, getBoxStatusInfo, getButtonInfo }) {
    const dispatch = useDispatch();

    const { changeResponse, changeError, changeLoading } = useSelector(({ database, loading }) => ({
        changeResponse: database.changeResponse,
        changeError: database.changeError,
        changeLoading: loading['database/CHANGE_ORDER']
    }));
    
    // 드래그된 아이템 관리
    const [localResponse, setLocalResponse] = useState(response);
    const [draggedItem, setDraggedItem] = useState(null);
    
    useEffect(() => {
        setLocalResponse(response);
    }, [response]);
    
    useEffect(() => {
        if (!changeLoading && changeResponse && draggedItem) {
            setDraggedItem(null);
        }

        if (!changeLoading && changeError && draggedItem) {
            setLocalResponse(response);
            setDraggedItem(null);
        }
    }, [changeResponse, changeError, changeLoading, draggedItem, response]);

    // 센서 설정 (마우스, 키보드, 터치)
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            const oldIndex = localResponse.findIndex(item => item.id === active.id);
            const newIndex = localResponse.findIndex(item => item.id === over.id);
            
            const draggedItemData = localResponse[oldIndex];
            const targetOrder = newIndex + 1;
            
            // 로컬 상태 먼저 업데이트 (즉시 UI 반영)
            const newResponse = arrayMove(localResponse, oldIndex, newIndex);
            setLocalResponse(newResponse);
            
            // 드래그된 아이템 정보 저장
            setDraggedItem({
                id: draggedItemData.id,
                targetOrder,
                oldIndex,
                newIndex
            });
            
            dispatch(changeOrder({ 
                id: draggedItemData.id, 
                targetOrder 
            }));
        }
    };

    return (
        <div className={styles.wrapperCardList}>
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={localResponse ? localResponse.map(item => item.id) : []}
                    strategy={rectSortingStrategy}
                >
                    {localResponse && localResponse.length > 0 && localResponse.map((item) => (
                        <SortableCard 
                            key={item.id} 
                            item={item}
                            setCurrentStepName={setCurrentStepName}
                            setConnectionInfo={setConnectionInfo}
                            toggleAddModal={toggleAddModal}
                            openDeleteModal={openDeleteModal}
                            getConnectionStatus={getConnectionStatus}
                            getBoxStatusInfo={getBoxStatusInfo}
                            getButtonInfo={getButtonInfo}
                        />
                    ))}
                </SortableContext>
            </DndContext>
        </div>
    );
}

const InfoRow = ({ items }) => (
    <div className={styles.row}>
        {items.map(({ label, value }) => (
            <InfoItem key={label} label={label} value={value} />
        ))}
    </div>
);

const InfoItem = ({ label, value }) => (
    <div className={styles.item}>
        <p className={styles.label}>{label}</p>
        <p className={styles.value}>{value}</p>
    </div>
);
